import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import bm from "../100-board-members/BoardMembers100.module.scss";
import Styles from "./ContactUs.module.scss";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { appLevelConstants } from "../../constants/AppLevelConstants";

/**
 * Iframe height: follow Zoho postMessage payloads. A huge initial height + always using
 * Math.max(prev, ...) left a pillar of empty branded (red) area inside the iframe.
 */
const IFRAME_H_MIN = 560;
const IFRAME_H_MAX = 1560;
/** Until Zoho posts a height — desktop vs mobile so small phones don’t get a towering blank embed. */
const IFRAME_H_INITIAL_DESKTOP = 720;
const IFRAME_H_INITIAL_MOBILE = 560;
/** Extra px after Zoho-reported body height (submit/footer padding). */
const IFRAME_H_PADDING = 44;

/** Client-only SPA: safe at state init when building in the browser bundle. */
function iframeInitialPxForViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return IFRAME_H_INITIAL_DESKTOP;
  }
  return window.matchMedia("(max-width: 767px)").matches
    ? IFRAME_H_INITIAL_MOBILE
    : IFRAME_H_INITIAL_DESKTOP;
}

function isZohoFormOrigin(origin) {
  const o = String(origin || "");
  return (
    o.includes("zoho.com") ||
    o.includes("zoho.in") ||
    o.includes("zoho.eu") ||
    o.includes("zohoforms.") ||
    o.includes("forms.iamironlady.com") ||
    o.includes("forms.zoho") ||
    o.includes("forms.zohopublic")
  );
}

function normalizeHeightPayload(data) {
  if (!data || typeof data !== "object") return null;
  const keys = [
    "height",
    "frameHeight",
    "formHeight",
    "form_body_height",
    "if_height",
    "iframe_height",
    "zf_form_height",
    "contentHeight",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const v = data[keys[i]];
    if (v == null) continue;
    const n =
      typeof v === "number" ? v : Number.parseFloat(String(v).replace(/px/i, ""));
    if (Number.isFinite(n) && n > 120) return n;
  }
  return null;
}

function clampToFormIframe(rawPx) {
  const withPad = rawPx + IFRAME_H_PADDING;
  return Math.min(IFRAME_H_MAX, Math.max(IFRAME_H_MIN, Math.round(withPad)));
}

export default function ContactUs() {
  const zohoIframeSrc = appLevelConstants.forms.letsConnectZohoIframeSrc;
  const zohoPageUrl = appLevelConstants.forms.letsConnectZohoPageUrl;
  const [iframePxHeight, setIframePxHeight] = useState(iframeInitialPxForViewport);

  useEffect(() => {
    function onMessage(event) {
      if (!isZohoFormOrigin(event.origin)) return;
      let parsed = normalizeHeightPayload(event.data);
      if (parsed == null && typeof event.data === "string" && event.data.trim().startsWith("{")) {
        try {
          parsed = normalizeHeightPayload(JSON.parse(event.data));
        } catch {
          return;
        }
      }
      if (parsed == null) return;
      const next = clampToFormIframe(parsed);
      setIframePxHeight(next);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const scrollToZohoForm = useCallback(() => {
    document
      .getElementById("lets-connect-zoho")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const contactCards = [
    {
      icon: FaPhone,
      label: "Phone",
      href: "tel:+917360823123",
      value: "+91 · 73608 23123",
    },
    {
      icon: FaEnvelope,
      label: "Email",
      href: "mailto:admin@iamironlady.com",
      value: "admin@iamironlady.com",
    },
    {
      icon: FaMapMarkerAlt,
      label: "Office",
      value:
        "Nandi Info Tech, 2nd Floor, KIADB plot#8, post, 1st cross, Sadaramangala Rd, Mahadevapura, Bengaluru, Karnataka — 560048.",
    },
  ];

  return (
    <div className={bm["page-wrapper"]}>
      <Helmet>
        <title>Contact Us | Iron Lady</title>
        <meta
          name="description"
          content="Reach Iron Lady — programs, partnerships, or questions. Phone, email, Bengaluru office, or send a message. We empower women leaders across India."
        />
        <link rel="canonical" href="https://iamironlady.com/contactUs" />
      </Helmet>

      <section className={bm.hero} aria-labelledby="contact-hero-heading">
        <div className={bm["hero-overlay"]} aria-hidden />
        <div className={bm["hero-inner"]}>
          <div className={bm["hero-content"]}>
            <div className={bm["hero-eyebrow"]}>Contact</div>
            <div className={bm["hero-brand"]}>Iron Lady</div>
            <h1 id="contact-hero-heading" className={bm["hero-tagline"]}>
              Let&apos;s
              <span>connect.</span>
            </h1>
            <p className={bm["hero-sub"]}>
              Programs, collaborations, press, or anything on your mind—tell us below and we&apos;ll
              reply with clear next steps. Prefer a live chat? Reach us anytime by phone or email.
            </p>
            <div className={`${bm["hero-cta"]} ${bm["hero-cta-group"]}`}>
              <button
                type="button"
                className={bm["btn-primary"]}
                onClick={scrollToZohoForm}
              >
                <span>Go to Let&apos;s Connect form</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section
        id="lets-connect-zoho"
        className={`${bm["video-section"]} ${Styles.zohoFormSection}`}
        aria-labelledby="lets-connect-zoho-heading"
      >
        <div className={Styles.zohoFormInner}>
          {/*
           * Zoho’s embed already renders the “Let’s Connect” heading.
           * Keep one accessible landmark here without doubling the headline visually.
           */}
          <h2 id="lets-connect-zoho-heading" className={Styles.zohoSrHeading}>
            Let&apos;s Connect form
          </h2>
          <div className={Styles.zohoFrameShell}>
            <iframe
              title="Iron Lady — Let's Connect form"
              aria-label="Iron Lady Let's Connect contact form"
              src={zohoIframeSrc}
              className={Styles.zohoIframe}
              allow="fullscreen"
              style={{
                height: `${iframePxHeight}px`,
              }}
            />
          </div>
          <p className={Styles.zohoFormFallback}>
            If the form does not appear,{" "}
            <a href={zohoPageUrl} target="_blank" rel="noopener noreferrer">
              open Let&apos;s Connect in a new tab
            </a>
            .
          </p>
        </div>
      </section>

      <section
        className={`${bm.authority} ${Styles.contactSection}`}
        aria-labelledby="contact-reach-heading"
      >
        <div className={`${bm["authority-grid"]} ${Styles.contactGrid}`}>
          <div className={`${bm["authority-left"]} ${Styles.contactCopy}`}>
            <span className={bm.eyebrow}>Reach us</span>
            <h2 id="contact-reach-heading">
              Break barriers —
              <br />
              with us.
            </h2>
            <p className={Styles.contactLead}>
              We&apos;re committed to empowering women across India to lead with clarity and
              courage. Join a program, explore a partnership, or ask anything—the form below is the
              best way to send structured details straight to our team.
            </p>
            <p className={Styles.contactSecondary}>
              Prefer talking first? Phone or email work great for quick questions—you can always send a
              structured note through the form when you&apos;re ready.
            </p>
          </div>

          <ul className={Styles.contactCards}>
            {contactCards.map(({ icon: Icon, label, href, value }) => (
              <li key={label} className={Styles.contactCard}>
                <div className={Styles.contactCardIcon} aria-hidden>
                  <Icon />
                </div>
                <div className={Styles.contactCardBody}>
                  <span className={Styles.contactCardLabel}>{label}</span>
                  {href ? (
                    <a className={Styles.contactCardValue} href={href}>
                      {value}
                    </a>
                  ) : (
                    <span className={`${Styles.contactCardValue} ${Styles.contactCardAddress}`}>
                      {value}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
